import { useEffect, useState } from "react";
import { Save, Loader2, MapPin, Info } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminTravelFees() {
    const [cfg, setCfg] = useState(null);
    const [saving, setSaving] = useState(false);
    const [preview, setPreview] = useState(null);

    useEffect(() => {
        (async () => {
            const { data } = await api.get("/pricing-config");
            setCfg({
                travel_free_km: data.travel_free_km ?? 10,
                travel_bracket_km: data.travel_bracket_km ?? 10,
                travel_per_bracket: data.travel_per_bracket ?? 10,
                default_travel_fee: data.default_travel_fee ?? 0,
                // preserve other fields
                condition_surcharges: data.condition_surcharges || {},
                disinfection_surcharge: data.disinfection_surcharge ?? 0,
                impermeabilization_surcharge: data.impermeabilization_surcharge ?? 0,
                currency: data.currency || "EUR",
                minimum_order: data.minimum_order ?? 0,
            });
        })();
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/admin/pricing-config", cfg);
            toast.success("Configuração guardada");
            recomputePreview();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };

    const recomputePreview = () => {
        if (!cfg) return;
        const brackets = [5, 10, 15, 20, 25, 30, 40, 50, 75, 100];
        setPreview(brackets.map((km) => {
            const free = Number(cfg.travel_free_km);
            const bk = Number(cfg.travel_bracket_km);
            const per = Number(cfg.travel_per_bracket);
            if (km <= free) return { km, fee: 0 };
            const extra = km - free;
            const n = Math.ceil(extra / bk);
            return { km, fee: +(n * per).toFixed(2) };
        }));
    };

    useEffect(() => { recomputePreview(); /* eslint-disable-next-line */ }, [cfg?.travel_free_km, cfg?.travel_bracket_km, cfg?.travel_per_bracket]);

    if (!cfg) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-white/40" /></div>;

    return (
        <div className="space-y-6" data-testid="admin-travel">
            <div>
                <h1 className="font-heading text-3xl font-bold tracking-tight">Taxas de deslocação</h1>
                <p className="text-white/60">Calculadas por distância real (rotas Google Maps / OSM) a partir da loja em Meinedo.</p>
            </div>

            <div className="ab-card p-8">
                <div className="flex items-start gap-3 p-4 rounded-none bg-[#C9A961]/10 border border-[#C9A961]/30 mb-6">
                    <Info className="w-5 h-5 text-[#C9A961] flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-white/80">
                        A distância é calculada em tempo real com base na morada indicada pelo cliente. As deslocações dentro do raio gratuito não têm custo. A partir daí, cada intervalo de km adiciona uma taxa fixa.
                    </div>
                </div>

                <div className="grid sm:grid-cols-3 gap-6">
                    <div>
                        <Label>Raio gratuito (km)</Label>
                        <Input type="number" step="1" min={0} value={cfg.travel_free_km} onChange={(e) => setCfg({ ...cfg, travel_free_km: Number(e.target.value) })} data-testid="tf-free" />
                        <p className="text-xs text-white/50 mt-1">Distância sem custo de deslocação.</p>
                    </div>
                    <div>
                        <Label>Intervalo (km)</Label>
                        <Input type="number" step="1" min={1} value={cfg.travel_bracket_km} onChange={(e) => setCfg({ ...cfg, travel_bracket_km: Number(e.target.value) })} data-testid="tf-bracket" />
                        <p className="text-xs text-white/50 mt-1">A cada quantos km incrementa o valor.</p>
                    </div>
                    <div>
                        <Label>Valor por intervalo (€)</Label>
                        <Input type="number" step="0.5" min={0} value={cfg.travel_per_bracket} onChange={(e) => setCfg({ ...cfg, travel_per_bracket: Number(e.target.value) })} data-testid="tf-per" />
                        <p className="text-xs text-white/50 mt-1">Valor adicionado por cada intervalo.</p>
                    </div>
                </div>

                <div className="mt-8 pt-8 border-t border-[#2A2A2A]">
                    <Label>Taxa fallback (se não for possível calcular distância)</Label>
                    <Input type="number" step="0.5" min={0} value={cfg.default_travel_fee} onChange={(e) => setCfg({ ...cfg, default_travel_fee: Number(e.target.value) })} data-testid="tf-fallback" className="max-w-xs" />
                    <p className="text-xs text-white/50 mt-1">Aplicado quando a morada não pode ser geolocalizada.</p>
                </div>
            </div>

            <div className="ab-card p-8">
                <h2 className="font-heading font-bold text-lg mb-4 flex items-center gap-2"><MapPin className="w-4 h-4 text-[#C9A961]" /> Pré-visualização de preços por distância</h2>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    {preview?.map((p) => (
                        <div key={p.km} className="border border-[#2A2A2A] p-4 rounded-none">
                            <div className="text-xs text-white/50 uppercase tracking-widest">até {p.km} km</div>
                            <div className="font-heading font-black text-xl text-[#C9A961] mt-1">{p.fee.toFixed(2)} €</div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="flex justify-end">
                <Button onClick={save} disabled={saving} className="ab-btn-primary rounded-none uppercase tracking-widest" data-testid="save-travel">
                    {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Guardar alterações
                </Button>
            </div>
        </div>
    );
}
